/**
 * @fileoverview Navigation module for the Curtis Institute of Music WordPress theme.
 * This module manages the site's main navigation menu functionality including:
 * - Mobile menu toggle behavior
 * - Submenu expand/collapse interactions
 * - Body scroll locking when menu is open
 * - Accessible menu state management
 * 
 * @module navigation
 */

/**
 * Interface for DOM elements used by the navigation module
 * @interface Elements
 */
type Elements = {
  /** The document body element */
  body: HTMLElement | null;
  /** The site header element */
  header: HTMLElement | null;
  /** The main navigation element */
  nav: HTMLElement | null;
  /** Array of menu toggle buttons */
  menuToggles: HTMLButtonElement[];
  /** Array of top-level menu items */
  menuItems: HTMLLIElement[];
};

/**
 * Navigation module interface
 * @interface Navigation
 */
type Navigation = {
  /** DOM elements used by the navigation */
  elements: Elements;
  /** Initialize the navigation module */
  init: () => void;
  /** Bind event listeners */
  bindEvents: () => void;
  /** Handle menu toggle button clicks */
  handleMenuToggleClick: (toggle: HTMLButtonElement) => void;
  /** Handle submenu toggle clicks */
  handleMenuItemClick: (item: HTMLLIElement) => void;
};

/**
 * Navigation module implementation that manages menu interactions
 * @type {Navigation}
 */
const navigation: Navigation = {
  /**
   * DOM elements used by the navigation
   * @property {HTMLElement|null} body - Document body
   * @property {HTMLElement|null} header - Site header
   * @property {HTMLElement|null} nav - Main navigation
   * @property {HTMLButtonElement[]} menuToggles - Menu toggle buttons
   * @property {HTMLLIElement[]} menuItems - Top-level menu items
   */
  elements: {
    body: document.querySelector('body'),
    header: document.querySelector('.site-header'),
    nav: document.querySelector('.site-menu nav'),
    menuToggles: Array.from(document.querySelectorAll('[data-action="menu-toggle"]')),
    menuItems: Array.from(document.querySelectorAll('.site-menu nav > ul > li'))
  },

  /**
   * Initializes the navigation module
   * Checks for required elements and sets up event bindings
   * 
   * @returns {void}
   * @example
   * navigation.init(); // Initializes menu functionality
   */
  init() {
    if (!this.elements.body || !this.elements.header || !this.elements.nav || !this.elements.menuToggles.length) {
      return;
    }

    this.bindEvents();
  },

  /**
   * Binds event listeners to menu elements
   * Sets up click handlers for menu toggles and submenu items
   * 
   * @returns {void}
   */
  bindEvents() {
    /* Set up the menu toggle(s) */
    this.elements.menuToggles.forEach((button) => {
      button.addEventListener('click', () => this.handleMenuToggleClick(button));
    });

    /* Set up the menu items */
    this.elements.menuItems.forEach((item) => {
      const button = item.querySelector('button[data-action="submenu-toggle"]');

      if(!button) {
        return;
      }

      button.addEventListener('click', () => this.handleMenuItemClick(item));
    });
  },

  /**
   * Handles menu toggle button clicks
   * Opens/closes the mobile menu and manages body scroll locking
   * 
   * @param {HTMLButtonElement} toggle - The clicked toggle button
   * @returns {void}
   * @example
   * // User clicks menu button
   * navigation.handleMenuToggleClick(menuButton); // Opens menu
   * navigation.handleMenuToggleClick(menuButton); // Closes menu
   */
  handleMenuToggleClick(toggle) {
    const label = toggle.querySelector('.site-menu__toggle--label');
    this.elements.header?.classList.toggle('site-menu--is-open');

    if (!label) {
      return;
    }

    if (this.elements.header?.classList.contains('site-menu--is-open')) {
      this.elements.body?.classList.add('no-scroll');
      label.textContent = 'Close';
    } else {
      this.elements.body?.classList.remove('no-scroll');
      label.textContent = 'Menu';
    }
  },

  /**
   * Handles submenu toggle clicks
   * Manages the open/closed states of submenus with proper visual states
   * When opening a submenu, closes others and adds visual indicators
   * When closing, resets all visual states
   * 
   * @param {HTMLLIElement} activeItem - The menu item that was clicked
   * @returns {void}
   * @example
   * // User clicks a menu item with submenu
   * navigation.handleMenuItemClick(menuItem); // Opens submenu, closes others
   * navigation.handleMenuItemClick(menuItem); // Closes submenu, resets all states
   */
  handleMenuItemClick(activeItem) {
    const wasOpen = activeItem.classList.contains('site-menu__item--is-open');

    this.elements.menuItems.forEach((item) => {
      if (wasOpen) {
        // If the active item was open (and will close), remove 'is-closed' and 'is-open' from all items
        item.classList.remove('site-menu__item--is-closed');
        item.classList.remove('site-menu__item--is-open');
      } else {
        // If the active item was closed (and will open), handle each item appropriately
        if (item !== activeItem) {
          item.classList.add('site-menu__item--is-closed');
          item.classList.remove('site-menu__item--is-open');
        } else {
          item.classList.remove('site-menu__item--is-closed');
        }
      }
    });

    // Toggle the open state of the active item
    if (!wasOpen) {
      activeItem.classList.add('site-menu__item--is-open');
    }
  }

};

export default navigation;
